import { useNavigate } from "react-router-dom";

import { useId, useMemo, useRef, useState } from "react";
import { getSellerPath } from "@/lib/routing/portal-urls";

import { useTranslations } from "@/lib/i18n/intl";
import type { DefaultValues } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";
import { keys } from "@/lib/i18n/portal";
import { useCreateCategoryMutation } from "@/modules/categories/services/useCreateCategoryMutation";
import { useUpdateCategoryMutation } from "@/modules/categories/services/useUpdateCategoryMutation";
import { useFloatingActionBar } from "@/lib/hooks/useFloatingActionBar";
import { useFormSafety } from "@/lib/navigation/useFormSafety";
import { Alert, DiscardChangesDialog, FormActionButtons, FormField, NumberInput, TextInput } from "@a2z/ui";

type FormProps = {
  locale: string;
  storeSlug: string;
  mode: "create" | "edit";
  categoryId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  redirectOnSuccess?: boolean;
  enableFloatingActions?: boolean;
  defaultValues?: {
    nameAr?: string;
    nameEn?: string | null;
    sortOrder?: number;
  };
};

export function CategoryForm({
  locale,
  storeSlug,
  mode,
  categoryId,
  defaultValues,
  onSuccess,
  onCancel,
  redirectOnSuccess = true,
  enableFloatingActions = true,
}: FormProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const formId = useId();
  const actionRef = useRef<HTMLDivElement | null>(null);
  const initialValues = useMemo<DefaultValues<{
    nameAr: string;
    nameEn: string;
    sortOrder: string;
  }>>(
    () => ({
      nameAr: defaultValues?.nameAr ?? "",
      nameEn: defaultValues?.nameEn ?? "",
      sortOrder:
        typeof defaultValues?.sortOrder === "number"
          ? String(defaultValues.sortOrder)
          : "",
    }),
    [defaultValues?.nameAr, defaultValues?.nameEn, defaultValues?.sortOrder]
  );
  const {
    register,
    handleSubmit,
    setError: setFieldError,
    clearErrors,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<{ nameAr: string; nameEn: string; sortOrder: string }>({
    defaultValues: initialValues,
    mode: "onChange",
  });
  const [savedValues, setSavedValues] = useState(initialValues);
  const watchedValues = useWatch({
    control,
    defaultValue: {
      nameAr: initialValues.nameAr ?? "",
      nameEn: initialValues.nameEn ?? "",
      sortOrder: initialValues.sortOrder ?? "",
    },
  });
  const hasUnsavedChanges = useMemo(() => {
    return (
      (watchedValues?.nameAr ?? "") !== (savedValues.nameAr ?? "") ||
      (watchedValues?.nameEn ?? "") !== (savedValues.nameEn ?? "") ||
      (watchedValues?.sortOrder ?? "") !== (savedValues.sortOrder ?? "")
    );
  }, [
    savedValues.nameAr,
    savedValues.nameEn,
    savedValues.sortOrder,
    watchedValues?.nameAr,
    watchedValues?.nameEn,
    watchedValues?.sortOrder,
  ]);
  const { promptDiscard, discardDialogState } = useFormSafety({
    isDirty: hasUnsavedChanges,
    onDiscard: () => {
      reset(savedValues);
      setError(null);
    },
  });
  const actionsVisible = hasUnsavedChanges || isSubmitting;
  const shouldFloatActions = useFloatingActionBar(
    actionRef,
    enableFloatingActions && actionsVisible
  );
  const handleCancel = () => {
    promptDiscard(() => (onCancel ? onCancel() : navigate(-1)));
  };
  const saveLabel =
    mode === "create" ? t(keys.seller.addCategoryAction) : t(keys.common.update);
  const { mutateAsync: createCategory } = useCreateCategoryMutation();
  const { mutateAsync: updateCategory } = useUpdateCategoryMutation();

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    clearErrors();

    const nameAr = values.nameAr.trim();
    const nameEn = values.nameEn.trim();
    // `register(..., { valueAsNumber: true })` yields NaN for an EMPTY input.
    // `String(NaN)` is "NaN" — truthy — so an untouched (optional) sort order
    // used to fall into the validation branch below and fail with "required",
    // making it impossible to create a category without typing a number.
    const rawSortOrder = values.sortOrder;
    const sortOrderRaw =
      rawSortOrder === undefined ||
      rawSortOrder === null ||
      (typeof rawSortOrder === "number" && Number.isNaN(rawSortOrder))
        ? ""
        : String(rawSortOrder).trim();
    let sortOrder: number | undefined;

    if (!nameAr) {
      setFieldError("nameAr", { type: "manual", message: t(keys.errors.required) });
      return;
    }
    if (sortOrderRaw) {
      const parsed = Number(sortOrderRaw);
      if (!Number.isInteger(parsed) || parsed < 0) {
        setFieldError("sortOrder", { type: "manual", message: t(keys.errors.required) });
        return;
      }
      sortOrder = parsed;
    }

    const payload = {
      nameAr,
      nameEn: nameEn.length > 0 ? nameEn : null,
      sortOrder,
    };

    try {
      if (mode === "create") {
        await createCategory({ storeSlug, ...payload });
      } else {
        await updateCategory({
          storeSlug,
          categoryId: categoryId ?? "",
          ...payload,
        });
      }
    } catch (caughtError) {
      setError(
        caughtError instanceof Error ? caughtError.message : t(keys.errors.generic),
      );
      return;
    }

    const nextValues = {
      nameAr,
      nameEn: values.nameEn.trim(),
      sortOrder: sortOrderRaw,
    };
    setSavedValues(nextValues);
    reset(nextValues);
    if (onSuccess) {
      onSuccess();
    }
    if (!redirectOnSuccess) {
      return;
    }
    navigate(
      `${getSellerPath(locale, storeSlug)}/categories?notice=category_saved`
      , { replace: true }
    );
  });

  return (
    <>
      <form
        id={formId}
        onSubmit={onSubmit}
        className="flex flex-col gap-6"
      >
        {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
        <FormField
          label={t(keys.categories.nameAr)}
          name="nameAr"
          required
          error={errors.nameAr?.message}
        >
          <TextInput
            placeholder={t(keys.placeholders.categoryNameAr)}
            required
            hasError={Boolean(errors.nameAr)}
            {...register("nameAr")}
          />
        </FormField>
        <FormField
          label={t(keys.categories.nameEn)}
          name="nameEn"
          error={errors.nameEn?.message}
        >
          <TextInput
            placeholder={t(keys.placeholders.categoryNameEn)}
            hasError={Boolean(errors.nameEn)}
            {...register("nameEn")}
          />
        </FormField>
        <FormField
          label={t(keys.categories.sortOrder)}
          name="sortOrder"
          helperText={t(keys.categories.sortOrderHelper)}
          error={errors.sortOrder?.message}
        >
          <NumberInput
            placeholder={t(keys.placeholders.sortOrder)}
            hasError={Boolean(errors.sortOrder)}
            {...register("sortOrder", { valueAsNumber: true })}
            min={undefined}
            max={undefined}
          />
        </FormField>
        <div
          ref={actionRef}
          className={`mt-6 overflow-hidden transition-all duration-300 ${
            actionsVisible
              ? "max-h-24 translate-y-0 opacity-100"
              : "pointer-events-none max-h-0 -translate-y-1 opacity-0"
          }`}
          aria-hidden={!actionsVisible}
        >
          <div className={actionsVisible ? "pb-1" : "pb-0"}>
            <FormActionButtons
              cancelLabel={t(keys.common.cancel)}
              saveLabel={saveLabel}
              loadingLabel={t(keys.common.loading)}
              onCancel={handleCancel}
              isSaving={isSubmitting}
              saveDisabled={false}
            />
          </div>
        </div>
      </form>
      {enableFloatingActions && shouldFloatActions && actionsVisible ? (
        <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 border-t border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-4 shadow-[var(--shadow-sm)] lg:bottom-0">
          <div className="mx-auto max-w-[640px]">
            <FormActionButtons
              formId={formId}
              cancelLabel={t(keys.common.cancel)}
              saveLabel={saveLabel}
              loadingLabel={t(keys.common.loading)}
              onCancel={handleCancel}
              isSaving={isSubmitting}
              saveDisabled={false}
            />
          </div>
        </div>
      ) : null}
      <DiscardChangesDialog
        {...discardDialogState}
        title={t(keys.forms.discardChangesTitle)}
        body={t(keys.forms.discardChangesBody)}
        discardLabel={t(keys.forms.discardChangesDiscard)}
        continueLabel={t(keys.forms.discardChangesContinue)}
      />
    </>
  );
}
